import { useState } from 'react';
import { FetchError } from '@sierralabs/react-material-ui';

import api, { ApiParams, FetchRequest } from '../../../../common/api/api';
import { apiResolve, RequestResult } from '../../../../common/api/apiResolve';
import { PresignedUrl, FileDto, CreateFileDto } from '../../Document.interface';

export function createFile(dto: CreateFileDto) {
  const request = api.post('files', dto);
  return { request, result: apiResolve<FileDto>(request) };
}

export function getPresignedUrl(
  folderPath: string,
  mimeType: string,
  token?: string
) {
  const accessToken = api.getAccessToken();
  // use token to check if guest, endpoint is used for
  // Supplier form uploads that have no cookie access token
  const guest = !accessToken && token ? '/guest' : '';
  const params: ApiParams = { folderPath, mimeType };
  if (!accessToken && token) {
    params.token = token;
  }
  const request = api.get('files/presign' + guest, params);
  return { request, result: apiResolve<PresignedUrl>(request) };
}

export type FileUploadStatus =
  | 'init'
  | 'loading'
  | 'uploading'
  | 'creating'
  | 'cancelled'
  | 'uploaded'
  | 'created'
  | 'presignError'
  | 'uploadError'
  | 'createError';

export interface FileUploaderItem<FileDto = any> {
  file: File;
  progress: number;
  status: FileUploadStatus;
  presignedUrl?: PresignedUrl;
  response?: Response; // upload response
  fileDto?: FileDto;
  error?: FetchError;
  abort?: () => void;
}

export type CreateDtoCallback<CreateFileDto = any> = (
  file: File,
  presignedUrl: PresignedUrl
) => CreateFileDto;

export interface FileUploader<FileDto = any, CreateFileDto = any> {
  items: FileUploaderItem<FileDto>[];
  clear: (skipCreated?: boolean) => void;
  cancel: (file: File) => void;
  find: (file: File) => FileUploaderItem<FileDto> | undefined;
  isUploaded: (file: File) => boolean;
  isUploading: (status: FileUploadStatus) => boolean;
  remove: (file: File) => void;
  upload: (files: File[], createDto?: CreateDtoCallback<CreateFileDto>) => void;
  uploadAsync: (
    files: File[],
    createDto?: CreateDtoCallback<CreateFileDto>
  ) => Promise<FileUploaderItem<FileDto>[]>;
}

export interface FileUploaderOptions<FileDto = any, CreateFileDto = any> {
  getPresignedUrl: (fileType: string) => {
    request: FetchRequest;
    result: Promise<RequestResult<PresignedUrl>>;
  };
  createFile?: (dto: CreateFileDto) => {
    request: FetchRequest;
    result: Promise<RequestResult<FileDto>>;
  };
  onUpdate?: () => void;
}

// non-hook implementation
// upload and create multiple files
export function createFileUploader<FileDto = any, CreateFileDto = any>(
  props: FileUploaderOptions<FileDto, CreateFileDto>
): FileUploader<FileDto, CreateFileDto> {
  type Item = FileUploaderItem<FileDto>;
  const { createFile, getPresignedUrl, onUpdate } = props;
  const items: Item[] = [];

  const updateState = (
    file: File,
    value: Partial<Item> | ((item: Item) => Partial<Item>)
  ) => {
    const index = items.findIndex(item => item.file === file);
    if (index < 0) {
      return items;
    }
    const item = items[index];
    const newValue = typeof value === 'function' ? value(item) : value;
    Object.assign(item, newValue);
    onUpdate?.();
  };

  const handlePresign = async (file: File) => {
    updateState(file, { status: 'loading' });
    const { request, result } = getPresignedUrl(file.type);
    updateState(file, { abort: () => request.abort() });
    const presign = await result;
    updateState(
      file,
      presign.data
        ? { progress: 1, presignedUrl: presign.data, abort: undefined }
        : { status: 'presignError', error: presign.error, abort: undefined }
    );
    return presign.data;
  };

  const handleUpload = (file: File, presignedUrl: PresignedUrl) => {
    const request = new XMLHttpRequest();
    updateState(file, { abort: () => request.abort() });
    return new Promise<boolean>(resolve => {
      let type: 'abort' | 'error' | 'load' = 'error';
      const done = () => {
        const item: Partial<Item> =
          type === 'load'
            ? { progress: 100, status: 'uploaded', response: request.response }
            : type === 'abort'
            ? { progress: 0, error: undefined, status: 'cancelled' }
            : {
                status: 'uploadError',
                error: {
                  statusCode: 500,
                  error: 'Upload Error',
                  message: 'An error occurred while uploading the file.'
                }
              };
        updateState(file, item);
        resolve(type === 'load');
      };

      const handleErrorEvent = () => (type = 'error');
      const handleAbortEvent = () => (type = 'abort');
      const handleLoadEvent = () => (type = 'load');
      request.addEventListener('error', handleErrorEvent);
      request.upload.addEventListener('error', handleErrorEvent);
      request.addEventListener('timeout', handleErrorEvent);
      request.upload.addEventListener('timeout', handleErrorEvent);
      request.addEventListener('abort', handleAbortEvent);
      request.upload.addEventListener('abort', handleAbortEvent);
      request.addEventListener('load', handleLoadEvent);
      // note that for request errors, request.upload 'load' event is
      // also triggered just before request 'error' event is triggered
      request.upload.addEventListener('load', handleLoadEvent);
      request.addEventListener('loadend', done);
      request.upload.addEventListener('progress', e => {
        if (e.lengthComputable) {
          updateState(file, {
            status: 'uploading',
            progress: (e.loaded / e.total) * 100
          });
        }
      });
      request.open('PUT', presignedUrl.signedUrl);
      // request.timeout = 5000;
      request.setRequestHeader('Content-Type', file.type);
      request.responseType = 'text';
      request.send(file);
    });
  };

  // create file dto
  const handleCreate = async (file: File, dto: CreateFileDto) => {
    if (!createFile) {
      return;
    }
    updateState(file, { status: 'creating' });
    const { request, result } = createFile(dto);
    updateState(file, { abort: () => request.abort() });
    const { data, error } = await result;
    updateState(
      file,
      data
        ? { fileDto: data, status: 'created', abort: undefined }
        : { error, status: 'createError', abort: undefined }
    );
  };

  // handle presigned url, upload, and create file
  const handleFile = async (
    file: File,
    createDto?: CreateDtoCallback<CreateFileDto>
  ) => {
    const presignedUrl = await handlePresign(file);
    if (!presignedUrl) {
      return;
    }
    const didUpload = await handleUpload(file, presignedUrl);
    if (didUpload && createDto) {
      const dto = createDto(file, presignedUrl);
      await handleCreate(file, dto);
    }
  };

  const upload = (
    files: File[],
    createDto?: CreateDtoCallback<CreateFileDto>
  ) => {
    const newItems: FileUploaderItem<FileDto>[] = [];
    for (const file of files) {
      // filter existing files
      if (!items.some(item => item.file === file)) {
        newItems.push({ file, status: 'init', progress: 0 });
      }
    }
    items.push(...newItems);
    onUpdate?.();
    newItems.forEach(item => handleFile(item.file, createDto));
  };

  const uploadAsync = async (
    files: File[],
    createDto?: CreateDtoCallback<CreateFileDto>
  ) => {
    const newItems: FileUploaderItem<FileDto>[] = [];
    for (const file of files) {
      // filter existing files
      if (!items.some(item => item.file === file)) {
        newItems.push({ file, status: 'init', progress: 0 });
      }
    }
    items.push(...newItems);
    onUpdate?.();
    for (const item of newItems) {
      await handleFile(item.file, createDto);
    }
    return newItems;
  };

  const cancel = (file: File) => {
    updateState(file, item => {
      item.abort?.();
      return { progress: 0, error: undefined, status: 'cancelled' };
    });
  };

  const remove = (file: File) => {
    const index = items.findIndex(item => item.file === file);
    if (index < 0) {
      return;
    }
    const item = items[index];
    item.abort?.();
    items.splice(index, 1);
    onUpdate?.();
  };

  const clear = (skipCreated = false) => {
    const filtered = items.filter(item => {
      if (skipCreated && item.fileDto) {
        return true;
      }
      item.abort?.();
      return false;
    });
    items.splice(0, items.length, ...filtered);
    onUpdate?.();
  };

  const isUploaded = (file: File) => {
    return items.some(item => {
      return (
        item.file === file && ['created', 'uploaded'].includes(item.status)
      );
    });
  };

  const isUploading = (status: FileUploadStatus) => {
    const match: FileUploadStatus[] = ['loading', 'uploading', 'creating'];
    return match.includes(status);
  };

  const find = (file: File) => items.find(item => item.file === file);

  const uploader: FileUploader<FileDto, CreateFileDto> = {
    items,
    cancel,
    clear,
    find,
    isUploaded,
    isUploading,
    remove,
    upload,
    uploadAsync
  };

  return uploader;
}

export function useFileUploader(folderPath = 'documents', token?: string) {
  const [uploader, setUploader] = useState(() => {
    return createFileUploader<FileDto, CreateFileDto>({
      createFile: dto => createFile(dto),
      getPresignedUrl: fileType => getPresignedUrl(folderPath, fileType, token),
      // update state to re-render components when `uploader.items` get updated
      onUpdate: () => setUploader(uploader => ({ ...uploader }))
    });
  });
  return uploader;
}
